/**
 * DualListSelect — a clean dual-list "pick list" (mirrors the KC2 PrimeReact
 * PickList used for agent selection): an "Available" panel on the left and a
 * "Selected" panel on the right, each searchable, with move controls between.
 *
 * Reusable anywhere a multi-select from a large list is needed (agents, teams…).
 * Rows: click to highlight, double-click to move; the middle buttons move the
 * highlighted rows or all rows in either direction.
 */
import { useMemo, useState, type JSX } from 'react'
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

export interface DualListOption {
  value: string
  label: string
  sublabel?: string | undefined
}

interface DualListSelectProps {
  options: DualListOption[]
  /** Selected values (order preserved). */
  selected: string[]
  onChange: (values: string[]) => void
  availableLabel?: string
  selectedLabel?: string
  searchPlaceholder?: string
  /** List body height in px (each panel). */
  height?: number
  emptyText?: string
}

const R = 'rounded-[3px]'

function Panel({
  title,
  rows,
  highlighted,
  onToggleHighlight,
  onMoveOne,
  query,
  setQuery,
  searchPlaceholder,
  height,
  emptyText,
}: {
  title: string
  rows: DualListOption[]
  highlighted: Set<string>
  onToggleHighlight: (v: string) => void
  onMoveOne: (v: string) => void
  query: string
  setQuery: (v: string) => void
  searchPlaceholder: string
  height: number
  emptyText: string
}): JSX.Element {
  const filtered = query.trim()
    ? rows.filter((r) => r.label.toLowerCase().includes(query.trim().toLowerCase())
        || (r.sublabel ?? '').toLowerCase().includes(query.trim().toLowerCase()))
    : rows

  return (
    <div className={`flex min-w-0 flex-col overflow-hidden ${R} border border-gray-200 bg-white`}>
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/70 px-3 py-2">
        <span className="text-xs font-bold uppercase tracking-wide text-gray-500">{title}</span>
        <span className={`${R} bg-gray-200/70 px-1.5 py-0.5 text-[10px] font-bold text-gray-600`}>{rows.length}</span>
      </div>
      <div className="relative border-b border-gray-100 p-2">
        <Search className="absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          className={`w-full ${R} border border-gray-200 bg-gray-50 py-1.5 pl-8 pr-2 text-sm outline-none focus:border-[#DCE7F8] focus:bg-white focus:ring-2 focus:ring-[#EEF4FF]`}
        />
      </div>
      <div className="overflow-y-auto py-1" style={{ height }}>
        {filtered.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-gray-400">{emptyText}</div>
        ) : (
          filtered.map((r) => {
            const active = highlighted.has(r.value)
            return (
              <button
                key={r.value}
                type="button"
                onClick={() => onToggleHighlight(r.value)}
                onDoubleClick={() => onMoveOne(r.value)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                  active ? 'bg-[#EEF4FF] text-[#3366FF]' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="min-w-0 flex-1 truncate">
                  {r.label}
                  {r.sublabel && <span className="ml-1.5 text-xs text-gray-400">{r.sublabel}</span>}
                </span>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}

function MoveButton({
  onClick,
  disabled,
  children,
  title,
}: {
  onClick: () => void
  disabled: boolean
  children: JSX.Element
  title: string
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`flex h-8 w-8 items-center justify-center ${R} border border-gray-200 bg-white text-gray-500 transition-colors hover:border-[#3366FF] hover:bg-[#F5F7FF] hover:text-[#3366FF] disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  )
}

export default function DualListSelect({
  options,
  selected,
  onChange,
  availableLabel = 'Available',
  selectedLabel = 'Selected',
  searchPlaceholder = 'Search…',
  height = 220,
  emptyText = 'No items',
}: DualListSelectProps): JSX.Element {
  const [availQuery, setAvailQuery] = useState('')
  const [selQuery, setSelQuery] = useState('')
  const [availHi, setAvailHi] = useState<Set<string>>(new Set())
  const [selHi, setSelHi] = useState<Set<string>>(new Set())

  const selectedSet = useMemo(() => new Set(selected), [selected])
  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options])
  const available = useMemo(() => options.filter((o) => !selectedSet.has(o.value)), [options, selectedSet])
  const selectedRows = useMemo(
    () => selected.map((v) => byValue.get(v)).filter((o): o is DualListOption => !!o),
    [selected, byValue],
  )

  const toggle = (set: Set<string>, setSet: (s: Set<string>) => void, v: string): void => {
    const next = new Set(set)
    if (next.has(v)) next.delete(v)
    else next.add(v)
    setSet(next)
  }

  const addValues = (values: string[]): void => {
    if (values.length === 0) return
    const merged = [...selected]
    for (const v of values) if (!selectedSet.has(v)) merged.push(v)
    onChange(merged)
    setAvailHi(new Set())
  }
  const removeValues = (values: string[]): void => {
    if (values.length === 0) return
    const rm = new Set(values)
    onChange(selected.filter((v) => !rm.has(v)))
    setSelHi(new Set())
  }

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-3">
      <Panel
        title={`${availableLabel} (${available.length})`}
        rows={available}
        highlighted={availHi}
        onToggleHighlight={(v) => toggle(availHi, setAvailHi, v)}
        onMoveOne={(v) => addValues([v])}
        query={availQuery}
        setQuery={setAvailQuery}
        searchPlaceholder={searchPlaceholder}
        height={height}
        emptyText={emptyText}
      />

      <div className="flex flex-col items-center justify-center gap-2">
        <MoveButton onClick={() => addValues([...availHi])} disabled={availHi.size === 0} title="Move selected right">
          <ChevronRight className="h-4 w-4" />
        </MoveButton>
        <MoveButton onClick={() => addValues(available.map((o) => o.value))} disabled={available.length === 0} title="Move all right">
          <ChevronsRight className="h-4 w-4" />
        </MoveButton>
        <MoveButton onClick={() => removeValues([...selHi])} disabled={selHi.size === 0} title="Move selected left">
          <ChevronLeft className="h-4 w-4" />
        </MoveButton>
        <MoveButton onClick={() => removeValues(selected)} disabled={selected.length === 0} title="Move all left">
          <ChevronsLeft className="h-4 w-4" />
        </MoveButton>
      </div>

      <Panel
        title={`${selectedLabel} (${selectedRows.length})`}
        rows={selectedRows}
        highlighted={selHi}
        onToggleHighlight={(v) => toggle(selHi, setSelHi, v)}
        onMoveOne={(v) => removeValues([v])}
        query={selQuery}
        setQuery={setSelQuery}
        searchPlaceholder={searchPlaceholder}
        height={height}
        emptyText="Nothing selected yet"
      />
    </div>
  )
}
